import React, { useCallback, useEffect, useRef, useState } from "react";
import Pagination from "../../components/listing/Pagination";
import { LoadingContainer } from "../../components/FormBuilder/CustomForm/FormBuilder";
import { Col, Spinner } from "react-bootstrap";
import StyledText from "../../components/StyledComponents/StyledText";
import { CorporateTheme } from "../../utils/themes";
import { useNavigate } from "react-router-dom";
import ArrowIcon from "../../assets/icons/ArrowIcon";
import { ActionWrapper } from "./QueryCreation";
import SearchIcon from "../../assets/icons/SearchIcon";
import { StyledInput } from "../../components/listing/ExpandableSearch";
import SettingsIcon from "../../assets/icons/SettingsIcon";
import ChevronDownIcon from "../../assets/icons/ChevronDownIcon";
import styled from "styled-components";
import { getAccessToken } from "../auth/services/auth";
import { useToast } from "../../components/toast/ToastContext";
import DataTable from "../../components/listing/DataTable";
import { DropResult } from "@hello-pangea/dnd";
import AssignPrivateNoteModal from "./AssignPrivateNoteModal";
import AssignContactRoleModal from "./AssignContactRoleModal";
import { formatLabel } from "../../utils/helper";
import DeleteIcon from "../../assets/icons/DeleteIcon";
import DeletePrompt from "../../components/DeletePrompt";
import { getQueryScopeState } from "./QuerySlice";
import { useSelector } from "react-redux";
import { useBulkDeleteMutation } from "../../api/searchQueryApiSlice";
import { useTheme } from "../../middleware/ThemeContext";
import { ActionType } from "../../types/types";
import SyncIcon from "../../assets/icons/SyncIcon";
import BulkUpdateModal from "./BulkUpdateModal";
import LoadingCircleIcon from "../../assets/icons/LoadingCircleIcon";

interface QueryResultsListingProps {
  tableConfig: any;
  setTableConfig: React.Dispatch<React.SetStateAction<any>>;
  setIsRun: React.Dispatch<React.SetStateAction<boolean>>;
  shouldRun: boolean;
  runResult: any;
  setRunResult: any;
  tableColumns: any;
  folderId: string;
  tableData: any[]; // Added property
  runQueryLoading: boolean; // Added property
  queryId?: string;
  runQueryResults: (queryId: string) => void;
  scope?: string,
  queryResult?: any;
  resetRunResult: () => void;
}

const QueryResultsListing: React.FC<QueryResultsListingProps> = ({
  tableConfig,
  setTableConfig,
  setIsRun,
  shouldRun,
  runResult,
  setRunResult,
  folderId,
  tableColumns,
  tableData,
  runQueryLoading,
  runQueryResults,
  queryId,
  scope,
  queryResult,
  resetRunResult,
}) => {
  const { theme } = useTheme();
  const userID = JSON.parse(
      localStorage.getItem("authData") || "{}"
    )?.userId;
  const navigate = useNavigate();
  const formatRef = useRef(null);
  const showToast = useToast();
  const querydata = useSelector(getQueryScopeState);
  const [formatOptions, setFormatOptions] = useState(false);
  const [resultSelectedIds, setResultSelectedIds] = useState<number[]>([]);
  const [contactRolesModal, setContactRolesModal] = useState(false);
  const [privateNoteModal, setPrivateNoteModal] = useState(false);
  const [showDeleteResults, setShowDeleteResults] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [showBulkUpdateModal, setShowBulkUpdateModal] = useState(false);
  const [exportingFormat, setExportingFormat] = useState<string | null>(null);
  const [bulkDelete, { isLoading: bulkDeleteLoading }] =
    useBulkDeleteMutation();
  const fileFormats = [
    { label: "Excel (unformatted)", value: "EXCEL" },
    { label: "Excel (formatted)", value: "EXCEL formatted" },
    { label: "CSV", value: "CSV" },
    { label: "JSON", value: "JSON" },
    ...(scope !== 'organization' ? [
      { label: "Assign contact role", value: "contact role" },
      { label: "Add private note", value: "private note" },
    ] : []),
  ];

  const handleDragEnd = useCallback(
    (result: DropResult) => {
      if (!result.destination) return;

      const newColumnOrder = Array.from(tableConfig.columnOrder);
      const [removed] = newColumnOrder.splice(result.source.index, 1);
      newColumnOrder.splice(result.destination.index, 0, removed);

      setTableConfig((prev: any) => ({
        ...prev,
        columnOrder: newColumnOrder,
      }));
    },
    [tableConfig.columnOrder]
  );

  function useOutsideAlerter(ref: any) {
    useEffect(() => {
      function handleClickOutside(event: any) {
        if (ref.current && !ref.current.contains(event.target)) {
          setFormatOptions(false);
        }
      }

      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [ref]);
  }

  useOutsideAlerter(formatOptions && formatRef);

  const exportFiles = async (value: string) => {
    setExportingFormat(value);
    try {
      const params = new URLSearchParams(
        Object.fromEntries(
          Object.entries({
            format: value === "EXCEL formatted" ? "EXCEL" : value,
            ...(value === "EXCEL formatted" ? { formatted: "true" } : {}),
          }).filter(([_, v]) => v !== undefined)
        )
      );

      const token = getAccessToken(); // adjust if token is from Redux or cookies

      const response = await fetch(
        `${process.env.REACT_APP_API_URL
        }/api/search-query/export/${queryId}?${params.toString()}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        showToast("Error", "Failed to export file", "error");
        return;
      }

      const blob = await response.blob();

      // Extract filename from content-disposition header (optional)
      const contentDisposition = response.headers.get("Content-Disposition");
      const filenameMatch = contentDisposition?.match(/filename="?(.+)"?/);
      const filename =
        filenameMatch?.[1] ||
        `export.${value === "EXCEL formatted" || value === "EXCEL"
          ? "xlsx"
          : value.toLowerCase()
        }`;

      // Trigger file download
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      showToast("Success", "File downloaded successfully", "success");
    } catch (err) {
      console.error(err);
      showToast("Error", "Something went wrong while exporting", "error");
    } finally {
      setExportingFormat(null);
    }
  };

  const handleSelectAll = useCallback(() => {
    const normalizedData = tableData?.map((item) => {
      const idKey = Object.keys(item).find((key) => key.endsWith("id"));
      if (!idKey) return item; // if no matching key, return item as-is

      const { [idKey]: id, ...rest } = item;
      return {
        id,
        ...rest,
      };
    });

    // Convert all IDs to numbers
    const allIds = normalizedData?.map((item) => Number(item?.id)).filter((id) => !isNaN(id));

    const areAllSelected = allIds.every((id) => resultSelectedIds.includes(id));

    if (areAllSelected) {
      setResultSelectedIds([]);
    } else {
      setResultSelectedIds(allIds);
    }
  }, [tableData, resultSelectedIds]);

  const handleAction = async (action: ActionType, id: string) => {
    const numericId = Number(id);

    if (action === 'delete') {
      setDeleteId(id);
      setShowDeleteResults(true);
      return;
    }
    if (action === 'view') {
      navigate(`${numericId}`);
    }
  };
  const confirmDeleteResults = async () => {
    try {
      const { data }: any = await bulkDelete({
        recordIds: deleteId ? [deleteId] : resultSelectedIds,
        scope: scope,
      });
      if (data) {
        showToast(
          "Query Deleted",
          "The selected query has been deleted.",
          "success"
        );
        setShowDeleteResults(false);
        setResultSelectedIds([]);
        queryId && runQueryResults(queryId);
      }
    } catch {
      showToast(
        "Delete Failed",
        "Failed to delete query. Please try again.",
        "error"
      );
    }
  };
  return (
    <div
      className="row no-gutters"
      style={
        {
          "--bs-gutter-x": "0",
          height: "calc(100vh - 190px)",
        } as React.CSSProperties
      }
    >
      <Col lg={12} className="d-flex flex-column gap-2 p-3 " style={{ backgroundColor: theme.CorporateTheme.colors.darkLightBG }}>
        <div className="rounded p-2" style={{ backgroundColor: theme.CorporateTheme.colors.background }}>
          <div className="d-flex align-items-center justify-content-between">
            <StyledText
              className="ms-2"
              fontSize="16px"
              fontWeight={700}
              textColor={theme.CorporateTheme?.colors?.headerColor}
              // onClick={() => {
              //   shouldRun
              //     ? navigate(`/home/search-query`, {
              //       state: { runImmediately: false, paramId: folderId },
              //     })
              //     : setIsRun(false);
              // }}
              onClick={() => {
                if(shouldRun && userID === queryResult?.createdBy){
                  resetRunResult();
                  navigate(`/home/search-query/query-preview/${queryId}`, {
                    state: { item: queryResult, runImmediately: false },
                    replace: true,
                  });
                  setIsRun(false);
                } else if (shouldRun && userID != queryResult?.createdBy){
                  resetRunResult();
                  navigate(`/home/search-query`, {
                    state: { runImmediately: false, paramId: folderId },
                  })
                } else {
                  resetRunResult();
                  setIsRun(false);
              }}}
            >
              <ArrowIcon color={theme.CorporateTheme.colors.headerColor} rotate="left" className="cursor-pointer me-2" />
              Live Query Preview
            </StyledText>

            <div className="d-flex align-items-center gap-2 p-2">
              <ActionWrapper theme={theme} removeBoxShadow={true} height="38px" gap="12px">
                <SearchIcon color={theme.CorporateTheme.colors.textSecondary} />
                <StyledInput
                  theme={theme}
                  type="text"
                  placeholder="Search filter results"
                  value={runResult?.query}
                  onChange={(e: any) => {
                    setRunResult((prev: any) => ({
                      ...prev,
                      query: e.target?.value,
                    }));
                  }}
                />
              </ActionWrapper>

              <ActionWrapper
                height="38px"
                theme={theme}
                removeBoxShadow={true}
                removeBorder={true}
                borderRadius="3px"
                className="cursor-pointer position-relative"
                gap="8px"
                // onClick={() => setFormatOptions(true)}
                onClick={() => {
                  if (tableData?.length > 0) {
                    setFormatOptions(true);
                  }
                }}
                ref={formatRef}
                disabled={tableData?.length === 0}
              >
                <SettingsIcon size={18} color={theme.CorporateTheme.colors.gunmetal} /> <StyledText fontSize="14px" fontWeight={500} textColor={theme.CorporateTheme.colors.textSecondary}>Options</StyledText> <ChevronDownIcon color={theme.CorporateTheme.colors.textSecondary} />
                {formatOptions && (
                  <FormatOptions theme={theme}>
                    {fileFormats?.map((formats, ind) => (
                      <>
                        <Options
                          theme={theme}
                          className="py-2"
                          key={formats?.label}
                          onClick={() => {
                            if (formats?.label === "Assign contact role" && scope !== 'organization') {
                              resultSelectedIds?.length === 0
                                ? showToast(
                                  "Action Failed",
                                  "Select atleast 1 row.",
                                  "error"
                                )
                                : setContactRolesModal(true);
                            } else
                            if (formats?.label === "Add private note" && scope !== 'organization') {
                              resultSelectedIds?.length === 0
                                ? showToast(
                                  "Action Failed",
                                  "Select atleast 1 row.",
                                  "error"
                                )
                                : setPrivateNoteModal(true);
                            } else {
                              exportFiles(formats?.value);
                            }
                          }}
                        >
                          <span>{formats?.label} </span>
                          {exportingFormat === formats.value && (
                            <LoadingCircleIcon color={theme.CorporateTheme.colors.textSecondary} />
                          ) }
                        </Options>
                        {ind < fileFormats?.length - 1 && (
                          <hr className="my-0" />
                        )}
                      </>
                    ))}
                  </FormatOptions>
                )}
              </ActionWrapper>
              {resultSelectedIds?.length > 0 && (
                <>
                  <ActionWrapper
                    theme={theme}
                    className="cursor-pointer position-relative"
                    onClick={() => setShowBulkUpdateModal(true)}
                  >
                    <SyncIcon size={18} color={theme.CorporateTheme.colors.headerColor} />
                    Bulk Update
                  </ActionWrapper>

                  <BulkUpdateModal
                    recordIds={resultSelectedIds}
                    show={showBulkUpdateModal}
                    onClose={() => { setShowBulkUpdateModal(false); setResultSelectedIds([]) }}
                    scope={scope || "contact"} // Pass your columns data here
                  />
                </>
              )}
              {resultSelectedIds?.length > 0 && (
                <ActionWrapper
                  theme={theme}
                  className="cursor-pointer position-relative"
                  onClick={() => setShowDeleteResults(true)}
                ><DeleteIcon size={18} />
                  Delete
                </ActionWrapper>
              )}

              {/* <ActionWrapper className="cursor-pointer position-relative">
                          <DeleteIcon /> Delete
                        </ActionWrapper> */}
            </div>
          </div>

          {runQueryLoading ? (
            <LoadingContainer className="text-center p-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3">Loading queries...</p>
            </LoadingContainer>
          ) : (
            // <div
            //   className="mb-3"
            //   style={{ height: "calc(100vh - 390px)" }}
            // >
            <DataTable
              noAction
              hideIdColumn={true}
              title={scope === 'contact' || scope === 'crm' ? "Contacts Listing" : scope === 'organization' ? 'Organizations Listing' : 'Listing'}
              columns={tableColumns}
              data={tableData || []}
              columnOrder={tableConfig.columnOrder}
              visibleColumns={tableConfig.visibleColumns}
              sort={tableConfig.sort}
              showCheckboxColumn={true}
              noDataTitle="No Result Found"
              noDataSubtitle="Your query result list is currently empty. Start selecting fields to keep everything organized and easy to find."
              onDragEnd={handleDragEnd}
              loading={runQueryLoading}
              perPage={tableConfig.perPage}
              onAction={handleAction}
              minWidth="1000px"
              selectedIds={resultSelectedIds}
              onSelectAll={(ids: number[]) => { setResultSelectedIds(ids); }}
              onSelectRow={(id: number) => {
                setResultSelectedIds(prev => {
                  const newIds = prev.includes(id)
                    ? prev.filter(i => i !== id)
                    : [...prev, id];
                  return newIds;
                });
              }}
              containerHeight={360}
            />
            // </div>
          )}
        </div>
        {tableData?.length > 0 && (
          <Pagination
            className="mt-3 py-1"
            currentPage={runResult?.page ?? 1}
            totalItems={runResult?.total ?? 0}
            perPage={runResult?.perPage ?? 0}
            totalPages={runResult?.totalPages ?? 0}
            onPageChange={(page: number) =>
              setRunResult((prev: any) => ({ ...prev, page: page }))
            }
            onLimitChange={(newLimit: number) =>
              setRunResult((prev: any) => ({
                ...prev,
                perPage: newLimit,
                page: 1,
              }))
            }
          />
        )}
      </Col>
      {privateNoteModal && (
        <AssignPrivateNoteModal
          ids={resultSelectedIds.map(String)}
          scope={scope ?? ""}
          onClose={() => setPrivateNoteModal(false)}
        />
      )}

      {contactRolesModal && (
        <AssignContactRoleModal
          ids={resultSelectedIds.map(String)}
          refetch={() => queryId && runQueryResults(queryId)}
          onClose={() => setContactRolesModal(false)}
          setResultSelectedIds={(ids: string[]) => setResultSelectedIds(ids.map((id) => Number(id)))}
        />
      )}
      {showDeleteResults && (
        <DeletePrompt
          onConfirm={confirmDeleteResults}
          onCancel={() => {
            setShowDeleteResults(false);
          }}
          loading={bulkDeleteLoading}
        />
      )}
    </div>
  );
};

export default QueryResultsListing;
export const FormatOptions = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  position: 'absolute',
  top: '35px',
  right: '0px',
  width: '185px',
  borderWidth: '0.5px',
  padding: '10px',
  backgroundColor: theme.CorporateTheme.colors.background,
  borderBottomRightRadius: '5px',
  borderBottomLeftRadius: '5px',
  // boxShadow: '-4px 4px 40px 0px #0000001a',
  boxShadow: theme.CorporateTheme.shadow,
  border: '0.5px solid',
  // borderImageSource: 'linear-gradient(180deg, #babdc1 0%, #ffffff 100%)',
  borderImage: theme.CorporateTheme.linearGradient,
  borderImageSlice: 1,
  zIndex: 999,
}));

export const Options = styled('div')(({ theme }) => ({
  backgroundColor: theme.CorporateTheme.colors.background,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  '&:hover': {
    backgroundColor: theme.CorporateTheme.colors.labelBG, // light gray (Bootstrap light)
  },
}));
